import { useCallback, useEffect, useRef, useState } from 'react';

export const useCalendarFullscreen = ({
  panelVisible,
  calendarLoading,
}: {
  panelVisible: boolean;
  calendarLoading: boolean;
}): { isFullscreen: boolean; toggleFullscreen: () => void; fullscreenContainerRef: React.RefObject<HTMLDivElement> } => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const fullscreenContainerRef = useRef<HTMLDivElement>(null);

  const toggleFullscreen = useCallback(() => {
    setIsFullscreen((wasFullscreen) => !wasFullscreen);
  }, []);

  useEffect(() => {
    if (!isFullscreen) return;

    const handleKeyDown = (event: KeyboardEvent): void => {
      // Leave Escape to the reservation panel when it is open on top.
      if (event.key === 'Escape' && !panelVisible) setIsFullscreen(false);
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isFullscreen, panelVisible]);

  // The day-header row is sticky right below the toolbar, whose height varies
  // (safe areas, line wrapping on mobile). Publish the measured toolbar height
  // as a CSS variable so the SCSS can position the header. Re-attached on
  // fullscreen/loading changes since those swap the rendered toolbar.
  useEffect(() => {
    const container = fullscreenContainerRef.current;
    const toolbar = container?.querySelector('.rbc-toolbar');
    if (!container || !toolbar) return;

    const observer = new ResizeObserver(() => {
      container.style.setProperty('--calendar-toolbar-height', `${toolbar.getBoundingClientRect().height}px`);
    });
    observer.observe(toolbar);
    return () => observer.disconnect();
  }, [isFullscreen, calendarLoading]);

  return { isFullscreen, toggleFullscreen, fullscreenContainerRef };
};
